import { useMemo } from 'react';
import { toast } from 'sonner';

import { CAMPAIGN_SUPPORTED_PLATFORMS, CampaignLabelFilterOptions } from '@/entities/campaign';
import { useUpdateCampaignCreatorLabelMutation } from '@/features/campaign/hooks/mutation';
import { useCampaignCreatorCounts, useCampaignCreators } from '@/features/campaign/hooks/query';
import {
  getPlatformName,
  PLATFORM_LOGO_BY_TYPE,
  platformOptions,
} from '@/entities/public-filter/public-filter-constants';
import {
  type CampaignDashboardCreator,
  type CreatorFilterOption,
  type CreatorRow,
  type CreatorTabFilterRequest,
} from '@/features/campaign/ui/campaign-detail/hooks';
import { PageNavigationView } from '@/shared/components/page-navigation/page-navigation';
import { SearchInput } from '@/shared/components/search-input';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { TableBody, TableHead, TableHeader, TableRow } from '@/shared/components/shadcn/table';

import { Stage } from '@/shared/components/stage/stage';
import {
  ALL_LABELS_VALUE,
  ALL_PLATFORMS_VALUE,
  CREATORS_PAGE_SIZE,
  EMPTY_LABEL_VALUE,
} from '../../detail-constants';
import { CampaignDataTable, EmptyInlineState, FilterPills } from '../primitives';
import { CreatorTableRow } from './creator-table-row';
import { useCampaignCreatorFilter } from './use-campaign-creator-filter';

const EMPTY_CREATORS: CampaignDashboardCreator[] = [];
const campaignPlatformOptions = platformOptions.filter((option) =>
  CAMPAIGN_SUPPORTED_PLATFORMS.includes(option.value),
);

type Props = {
  campaignId: string;
  canEdit: boolean;
  filterRequest: CreatorTabFilterRequest | null;
  onOpenRequirementEditor: () => void;
};

export function CampaignDetailCreatorTab({
  campaignId,
  canEdit,
  filterRequest,
  onOpenRequirementEditor,
}: Props) {
  const creatorFilter = useCampaignCreatorFilter({ filterRequest });
  const creatorCountsQuery = useCampaignCreatorCounts(
    campaignId,
    creatorFilter.campaignCreatorRequestParams,
  );
  const creatorsQuery = useCampaignCreators(
    campaignId,
    creatorFilter.isPendingCreatorFilter,
    creatorFilter.campaignCreatorRequestParams,
    {
      pagination: {
        pageSize: CREATORS_PAGE_SIZE,
        resetKey: creatorFilter.paginationResetKey,
      },
    },
  );

  const updateCreatorLabelMutation = useUpdateCampaignCreatorLabelMutation();

  const creators = creatorsQuery.data?.items ?? EMPTY_CREATORS;
  const creatorRows = useMemo<CreatorRow[]>(() => {
    return creators.map((creator) => {
      return {
        ...creator,
        isMissing: creator.post_count === 0,
      };
    });
  }, [creators]);

  const creatorFilterPillOptions = [
    {
      label: 'All creators',
      value: 'all',
      count: creatorCountsQuery.data?.total ?? 0,
      isCountLoading:
        creatorCountsQuery.totalQuery.isLoading || creatorCountsQuery.totalQuery.isFetching,
    },
    {
      label: 'Pending',
      value: 'pending',
      count: creatorCountsQuery.data?.pending ?? 0,
      isCountLoading:
        creatorCountsQuery.pendingQuery.isLoading || creatorCountsQuery.pendingQuery.isFetching,
    },
  ] satisfies Array<{
    label: string;
    value: CreatorFilterOption;
    count: number;
    isCountLoading: boolean;
  }>;

  const onRetry = () => {
    void creatorsQuery.refetch();
    void creatorCountsQuery.refetch();
  };

  const onChangeLabel = async (creatorId: string, nextLabel: string) => {
    try {
      await updateCreatorLabelMutation.mutateAsync({
        campaignId,
        creatorId,
        label: nextLabel,
      });
    } catch {
      toast.error('Failed to update creator label');
    }
  };

  const selectedPlatform = creatorFilter.creatorPlatformFilter;
  const SelectedPlatformLogo = selectedPlatform ? PLATFORM_LOGO_BY_TYPE[selectedPlatform] : null;

  return (
    <>
      <div className="flex flex-col gap-6">
        <section className="space-y-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-foreground text-xl leading-6 font-semibold">Creator</h2>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!canEdit}
                onClick={onOpenRequirementEditor}
              >
                Edit creator
              </Button>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <FilterPills
                  options={creatorFilterPillOptions}
                  value={creatorFilter.creatorFilter}
                  onChange={(value) => creatorFilter.setCreatorFilter(value as CreatorFilterOption)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <SearchInput
                  className="h-9 w-full sm:w-50"
                  initialValue={creatorFilter.creatorSearchQuery}
                  onClear={() => creatorFilter.setCreatorSearchQuery('')}
                  onSubmit={creatorFilter.setCreatorSearchQuery}
                  placeholder="Search creators"
                />
                <Select
                  value={creatorFilter.creatorPlatformFilter ?? ALL_PLATFORMS_VALUE}
                  onValueChange={(value) =>
                    creatorFilter.setCreatorPlatformFilter(
                      value === ALL_PLATFORMS_VALUE
                        ? null
                        : (value as CampaignDashboardCreator['platform']),
                    )
                  }
                >
                  <SelectTrigger className="w-full sm:w-40">
                    {selectedPlatform && SelectedPlatformLogo ? (
                      <div className="flex items-center gap-2">
                        <SelectedPlatformLogo className="size-4" />
                        <span>{getPlatformName(selectedPlatform)}</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="All platforms" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PLATFORMS_VALUE}>All platforms</SelectItem>
                    {campaignPlatformOptions.map((option) => {
                      const PlatformLogo = PLATFORM_LOGO_BY_TYPE[option.value];

                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <PlatformLogo className="size-4" />
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Select
                  value={
                    creatorFilter.creatorLabelFilter === null
                      ? ALL_LABELS_VALUE
                      : creatorFilter.creatorLabelFilter || EMPTY_LABEL_VALUE
                  }
                  onValueChange={(value) =>
                    creatorFilter.setCreatorLabelFilter(value === ALL_LABELS_VALUE ? null : value)
                  }
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="All labels" />
                  </SelectTrigger>
                  <SelectContent>
                    {CampaignLabelFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Stage>
            <Stage.Scene status={creatorsQuery.isError}>
              <EmptyInlineState
                title="Creators are unavailable"
                description="We could not load creator tracking rows right now."
                action={
                  <Button variant="outline" size="sm" onClick={onRetry}>
                    Retry
                  </Button>
                }
              />
            </Stage.Scene>
            <Stage.Scene status={creatorsQuery.isFetching}>
              <EmptyInlineState
                title="Loading creators"
                description="Fetching the latest creator roster for this campaign."
              />
            </Stage.Scene>
            <Stage.Scene default>
              <CampaignDataTable>
                <TableHeader>
                  <TableRow gridTemplateColumns="minmax(240px,1.35fr) minmax(240px,1.35fr) minmax(156px,0.9fr) minmax(96px,0.55fr)">
                    <TableHead>Creator</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Posts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creatorRows.map((creator) => (
                    <CreatorTableRow
                      key={creator.id}
                      canEdit={canEdit}
                      creator={creator}
                      onChangeLabel={onChangeLabel}
                      onCopyEmail={handleCopyEmail}
                    />
                  ))}
                </TableBody>
              </CampaignDataTable>
            </Stage.Scene>
          </Stage>

          {!creatorsQuery.isError &&
          !creatorsQuery.isFetching &&
          creatorsQuery.pagination.shouldPaginate ? (
            <PageNavigationView
              className="justify-end px-0 py-0"
              startIndex={creatorsQuery.pagination.startIndex}
              endIndex={creatorsQuery.pagination.endIndex}
              totalCount={creatorsQuery.pagination.totalCount}
              onPrevious={creatorsQuery.pagination.goToPreviousPage}
              onNext={creatorsQuery.pagination.goToNextPage}
              canGoPrevious={creatorsQuery.pagination.canGoPrevious}
              canGoNext={creatorsQuery.pagination.canGoNext}
            />
          ) : null}
        </section>
      </div>
    </>
  );
}

async function handleCopyEmail(email: string) {
  try {
    await navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard');
  } catch {
    toast.error('Failed to copy email');
  }
}
